<script setup>
import { computed, ref } from 'vue'
import { useToast } from 'vue-toastification'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const emit = defineEmits(['submit'])
const toast = useToast()

const form = ref({
  apply_date: dayjs().format('YYYY-MM-DD'),
  start_time: '09:00',
  hours: 1,
  ot_type: 'compensatory',
  remark: null,
})

const submitting = ref(false)

const ot_type_options = [
  { value: 'compensatory', label: 'Compensatory Leave' },
  { value: 'money', label: 'Overtime Pay' },
]

const endTime = computed(() => {
  if (!form.value.start_time || !form.value.hours) return ''
  return dayjs(form.value.start_time, 'HH:mm')
    .add(form.value.hours, 'hours')
    .format('HH:mm')
})

const beginTimeISO = computed(() => {
  return form.value.apply_date + 'T' + form.value.start_time
})

const endTimeISO = computed(() => {
  return form.value.apply_date + 'T' + endTime.value
})

async function checkForm(e) {
  e.preventDefault()

  if (!form.value.start_time) {
    toast.error('Start time is required')
    return
  }

  if (!form.value.apply_date) {
    toast.error('Date is required')
    return
  }

  if (!form.value.hours || form.value.hours < 1) {
    toast.error('Hours must be at least 1')
    return
  }

  const payload = {
    begin_time: beginTimeISO.value,
    end_time: endTimeISO.value,
    ot_type: form.value.ot_type,
    remark: form.value.remark,
  }

  try {
    submitting.value = true
    const response = await axios.post('/api/overtimes', payload)
    toast.success(response.data.message)
    form.value = {
      apply_date: dayjs().format('YYYY-MM-DD'),
      start_time: '09:00',
      hours: 1,
      ot_type: 'compensatory',
      remark: null,
    }
    emit('submit')
  } catch (error) {
    toast.error(error.response?.data?.message ?? 'An error occurred')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <form
    class="w-full max-w-md mx-auto bg-white rounded-lg shadow-md p-6"
    @submit="checkForm"
  >
    <h2 class="text-xl font-semibold text-gray-800 mb-4">Apply Overtime</h2>

    <div class="mb-4">
      <label for="apply_date" class="block text-sm font-medium text-gray-700 mb-1">
        Date
      </label>
      <input
        id="apply_date"
        v-model="form.apply_date"
        type="date"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        required
      >
    </div>

    <!-- Start Time & Hours Row -->
    <div class="flex gap-4 mb-4">
      <div class="flex-1">
        <label for="start_time" class="block text-sm font-medium text-gray-700 mb-1">
          Start Time
        </label>
        <input
          id="start_time"
          v-model="form.start_time"
          type="time"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          required
        >
      </div>
      <div class="flex-1">
        <label for="hours" class="block text-sm font-medium text-gray-700 mb-1">
          Hours
        </label>
        <input
          id="hours"
          v-model.number="form.hours"
          type="number"
          min="1"
          step="1"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          required
        >
      </div>
    </div>

    <!-- End Time Display -->
    <div class="mb-4">
      <span class="block text-sm font-medium text-gray-500 mb-1">
        End Time (auto-calculated)
      </span>
      <div class="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-600">
        {{ endTime || '--:--' }}
      </div>
    </div>

    <!-- OT Type -->
    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 mb-2">Type</label>
      <div class="flex gap-4">
        <template v-for="option in ot_type_options" :key="option.value">
          <label class="flex items-center gap-2">
            <input
              required
              v-model="form.ot_type"
              name="ot_type"
              type="radio"
              :value="option.value"
              class="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
            >
            <span class="text-sm text-gray-700">{{ option.label }}</span>
          </label>
        </template>
      </div>
    </div>

    <!-- Remark -->
    <div class="mb-4">
      <label for="remark" class="block text-sm font-medium text-gray-700 mb-1">
        Reason
      </label>
      <textarea
        id="remark"
        required
        rows="4"
        v-model="form.remark"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
      ></textarea>
    </div>

    <!-- Submit -->
    <button
      type="submit"
      :disabled="submitting"
      class="w-full py-2 px-4 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {{ submitting ? 'Submitting...' : 'Submit' }}
    </button>
  </form>
</template>
